import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Chip, Paper, Stack, Typography } from '@mui/material';
import { Map } from '@parsimap/react-mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ApiClient from '../../../config/api';
import { useDispatch } from 'react-redux';
import { setIsLoading, setSnackBar } from '../../../store/globalSlice';
import openMissionDetail from './openMissionDetail';

const TEHRAN_CENTER = { lat: 35.7575, lng: 51.41 };
const PARSIMAP_TOKEN = (process.env.REACT_APP_PARSIMAP_API_KEY || '').replace(
  /"/g,
  ''
);
const SOURCE_ID = 'mission-points';
const LAYER_ID = 'mission-pins';
const LINE_SOURCE_ID = 'mission-route';
const LINE_LAYER_ID = 'mission-route-line';
const LINE_CASING_ID = 'mission-route-casing';
const ARROW_SOURCE_ID = 'mission-route-arrows';
const ARROW_LAYER_ID = 'mission-route-arrows';
const ARROW_IMAGE_ID = 'mission-route-arrow';
const pinImageId = (label) => `mission-num-pin-${label}`;

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const looksLikeIranLat = (value) => value >= 24 && value <= 41;
const looksLikeIranLng = (value) => value >= 43 && value <= 65;

const firstNumber = (...values) => {
  for (const value of values) {
    const parsed = toNumber(value);
    if (parsed !== null) return parsed;
  }
  return null;
};

export const getItemCoords = (item) => {
  let lat = firstNumber(
    item?.lat,
    item?.latitude,
    item?.location?.lat,
    item?.location?.latitude,
    item?.to?.location?.lat,
    item?.from?.location?.lat,
    item?.destination?.lat
  );
  let lng = firstNumber(
    item?.lon,
    item?.lng,
    item?.longitude,
    item?.location?.lon,
    item?.location?.lng,
    item?.location?.longitude,
    item?.to?.location?.lon,
    item?.to?.location?.lng,
    item?.from?.location?.lon,
    item?.from?.location?.lng,
    item?.destination?.lng,
    item?.destination?.lon
  );

  if (lat === null || lng === null) return null;
  if (lat === 0 && lng === 0) return null;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;

  // Some APIs send [lat, lon] swapped. Iran lat is ~25-40, lng is ~44-64.
  if (!looksLikeIranLat(lat) && looksLikeIranLat(lng) && looksLikeIranLng(lat)) {
    const swapped = lat;
    lat = lng;
    lng = swapped;
  }

  return { lat, lng };
};

const getMapBounds = (points) => {
  if (!points.length) return undefined;

  const lngs = points.map((point) => point.lng);
  const lats = points.map((point) => point.lat);
  const minLng = Math.min(...lngs);
  const minLat = Math.min(...lats);
  const maxLng = Math.max(...lngs);
  const maxLat = Math.max(...lats);
  const padLng = Math.max((maxLng - minLng) * 0.15, 0.004);
  const padLat = Math.max((maxLat - minLat) * 0.15, 0.004);

  return [
    [minLng - padLng, minLat - padLat],
    [maxLng + padLng, maxLat + padLat],
  ];
};

const pinSvg = (fill, label) => {
  const text = String(label);
  const fontSize = text.length > 1 ? 11 : 13;
  return `<svg xmlns="http://www.w3.org/2000/svg" width="64" height="84" viewBox="0 0 32 42">
    <path d="M16 1C7.716 1 1 7.716 1 16c0 11 15 25 15 25s15-14 15-25C31 7.716 24.284 1 16 1z" fill="${fill}" stroke="#ffffff" stroke-width="1.5" stroke-linejoin="round"/>
    <circle cx="16" cy="16" r="12.4" fill="#ffffff"/>
    <text x="16" y="20.6" text-anchor="middle" font-size="${fontSize}" font-weight="700" fill="#111111" font-family="Arial, Helvetica, sans-serif">${text}</text>
  </svg>`;
};

const loadPinImage = (map, id, fill, label) =>
  new Promise((resolve) => {
    if (map.hasImage(id)) {
      resolve();
      return;
    }

    const image = new Image(64, 84);
    image.onload = () => {
      if (!map.hasImage(id)) {
        map.addImage(id, image, { pixelRatio: 2 });
      }
      resolve();
    };
    image.onerror = () => resolve();
    image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
      pinSvg(fill, label)
    )}`;
  });

const loadPinImages = (map, points) => {
  const labels = [...new Set(points.map((point) => String(point.index + 1)))];
  return Promise.all(
    labels.map((label) => loadPinImage(map, pinImageId(label), '#111111', label))
  );
};

const toGeoJSON = (points, activePointId) => ({
  type: 'FeatureCollection',
  features: points.map((point) => ({
    type: 'Feature',
    geometry: {
      type: 'Point',
      coordinates: [point.lng, point.lat],
    },
    properties: {
      id: String(point.item.id),
      label: String(point.index + 1),
      icon: pinImageId(point.index + 1),
      index: point.index,
      active: String(point.item.id) === String(activePointId) ? 1 : 0,
    },
  })),
});

const toLineGeoJSON = (points) => {
  const ordered = [...points].sort((a, b) => a.index - b.index);
  return {
    type: 'FeatureCollection',
    features:
      ordered.length >= 2
        ? [
            {
              type: 'Feature',
              geometry: {
                type: 'LineString',
                coordinates: ordered.map((point) => [point.lng, point.lat]),
              },
            },
          ]
        : [],
  };
};

const toRad = (deg) => (deg * Math.PI) / 180;
const toDeg = (rad) => (rad * 180) / Math.PI;

const segmentBearing = (from, to) => {
  const φ1 = toRad(from.lat);
  const φ2 = toRad(to.lat);
  const Δλ = toRad(to.lng - from.lng);
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x =
    Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
};

const toArrowGeoJSON = (points) => {
  const ordered = [...points].sort((a, b) => a.index - b.index);
  return {
    type: 'FeatureCollection',
    features: ordered.slice(0, -1).map((from, index) => {
      const to = ordered[index + 1];
      return {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [(from.lng + to.lng) / 2, (from.lat + to.lat) / 2],
        },
        properties: {
          bearing: segmentBearing(from, to),
        },
      };
    }),
  };
};

const arrowSvg = () =>
  `<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24">
    <path d="M12 3 L21 19 L12 14.5 L3 19 Z" fill="#2061ef" stroke="#ffffff" stroke-width="1.6" stroke-linejoin="round"/>
  </svg>`;

const loadArrowImage = (map) =>
  new Promise((resolve) => {
    if (map.hasImage(ARROW_IMAGE_ID)) {
      resolve();
      return;
    }

    const image = new Image(48, 48);
    image.onload = () => {
      if (!map.hasImage(ARROW_IMAGE_ID)) {
        map.addImage(ARROW_IMAGE_ID, image, { pixelRatio: 2 });
      }
      resolve();
    };
    image.onerror = () => resolve();
    image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
      arrowSvg()
    )}`;
  });

const MissionsMapView = ({
  items = [],
  itemIndexes = {},
  emptyMessage = 'ماموریتی ثبت نشده است',
  setOpenDetail,
  setSelectedData,
  setSelectedTask,
  setSelectedTaskDetail,
  setMissionNo,
  getData,
}) => {
  const api = ApiClient();
  const dispatch = useDispatch();
  const mapRef = useRef(null);
  const containerRef = useRef(null);
  const pointsRef = useRef([]);
  const handlersBoundRef = useRef(false);
  const editingRef = useRef(false);
  const activePointIdRef = useRef(null);
  const [activePointId, setActivePointId] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [draftLocation, setDraftLocation] = useState(null);
 
  const points = useMemo(
    () =>
      items
        .map((item, index) => {
          const coords = getItemCoords(item);
          if (!coords) return null;
          const originalIndex = itemIndexes[item.id];
          return {
            ...coords,
            item,
            index: Number.isInteger(originalIndex) ? originalIndex : index,
          };
        })
        .filter(Boolean),
    [itemIndexes, items]
  );

  const displayPoints = useMemo(() => {
    if (!draftLocation) return points;
    return points.map((point) =>
      String(point.item.id) === String(draftLocation.id)
        ? { ...point, lat: draftLocation.lat, lng: draftLocation.lng }
        : point
    );
  }, [draftLocation, points]);

  pointsRef.current = displayPoints;
  editingRef.current = isEditing;
  activePointIdRef.current = activePointId;

  const bounds = useMemo(() => getMapBounds(points), [points]);
  const center = points[0] || TEHRAN_CENTER;
  const activePoint =
    displayPoints.find(
      (point) => String(point.item.id) === String(activePointId)
    ) || null;

  const handleSelect = (item, index) => {
    openMissionDetail({
      api,
      dispatch,
      item,
      index,
      setSelectedData,
      setSelectedTask,
      setSelectedTaskDetail,
      setMissionNo,
      setOpenDetail,
    });
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setDraftLocation(null);
  };

  const startEdit = (event) => {
    event.stopPropagation();
    if (!activePoint) return;
     setDraftLocation({
      id: activePoint.item.id,
      lat: activePoint.lat,
      lng: activePoint.lng,
    });
    setIsEditing(true);
  };

  const saveEdit = async (event) => {
    event.stopPropagation();
    if (!activePoint || !draftLocation) return;

    try {
      dispatch(setIsLoading(true));
      await api.put('/api/app/v1/shipito/tasks/edit-location', {
        id: Number(activePoint.item.id) || activePoint.item.id,
        type: 1,
         lat: draftLocation.lat,
        lng: draftLocation.lng,
      });
      dispatch(
        setSnackBar({
          message: 'موقعیت با موفقیت ویرایش شد.',
          severity: 'success',
        })
      );
      if (getData) await getData({ silent: true });
      setIsEditing(false);
      setDraftLocation(null);
    } catch (error) {
      console.error('Error editing location:', error);
      dispatch(
        setSnackBar({
          message: 'ویرایش موقعیت با خطا مواجه شد.',
          severity: 'error',
        })
      );
    } finally {
      dispatch(setIsLoading(false));
    }
  };

  const syncSource = (map) => {
    if (!map.getSource) return;
    const currentPoints = pointsRef.current;
    if (map.getSource(SOURCE_ID)) {
      map.getSource(SOURCE_ID).setData(toGeoJSON(currentPoints, activePointId));
    }
    if (map.getSource(LINE_SOURCE_ID)) {
      map.getSource(LINE_SOURCE_ID).setData(toLineGeoJSON(currentPoints));
    }
    if (map.getSource(ARROW_SOURCE_ID)) {
      map.getSource(ARROW_SOURCE_ID).setData(toArrowGeoJSON(currentPoints));
    }
  };

  const ensureLineLayer = (map) => {
    if (!map.getSource(LINE_SOURCE_ID)) {
      map.addSource(LINE_SOURCE_ID, {
        type: 'geojson',
        data: toLineGeoJSON(pointsRef.current),
      });
    } else {
      map.getSource(LINE_SOURCE_ID).setData(toLineGeoJSON(pointsRef.current));
    }

    const beforeId = map.getLayer(LAYER_ID) ? LAYER_ID : undefined;

    if (!map.getLayer(LINE_CASING_ID)) {
      map.addLayer(
        {
          id: LINE_CASING_ID,
          type: 'line',
          source: LINE_SOURCE_ID,
          layout: {
            'line-join': 'round',
            'line-cap': 'round',
          },
          paint: {
            'line-color': '#ffffff',
            'line-width': 7,
            'line-opacity': 0.95,
          },
        },
        beforeId
      );
    }

    if (!map.getLayer(LINE_LAYER_ID)) {
      map.addLayer(
        {
          id: LINE_LAYER_ID,
          type: 'line',
          source: LINE_SOURCE_ID,
          layout: {
            'line-join': 'round',
            'line-cap': 'round',
          },
          paint: {
            'line-color': '#2061ef',
            'line-width': 4,
          },
        },
        beforeId
      );
    }

    if (!map.getSource(ARROW_SOURCE_ID)) {
      map.addSource(ARROW_SOURCE_ID, {
        type: 'geojson',
        data: toArrowGeoJSON(pointsRef.current),
      });
    } else {
      map.getSource(ARROW_SOURCE_ID).setData(toArrowGeoJSON(pointsRef.current));
    }

    if (!map.getLayer(ARROW_LAYER_ID)) {
      map.addLayer(
        {
          id: ARROW_LAYER_ID,
          type: 'symbol',
          source: ARROW_SOURCE_ID,
          layout: {
            'icon-image': ARROW_IMAGE_ID,
            'icon-size': 0.7,
            'icon-rotate': ['get', 'bearing'],
            'icon-rotation-alignment': 'map',
            'icon-pitch-alignment': 'map',
            'icon-allow-overlap': true,
            'icon-ignore-placement': true,
            'icon-anchor': 'center',
          },
        },
        beforeId
      );
    }
  };

  const setupLayers = async (map) => {
    await loadPinImages(map, pointsRef.current);
    await loadArrowImage(map);

    if (!map.getSource(SOURCE_ID)) {
      map.addSource(SOURCE_ID, {
        type: 'geojson',
        data: toGeoJSON(pointsRef.current, activePointId),
      });
    } else {
      syncSource(map);
    }

    ensureLineLayer(map);

    if (!map.getLayer(LAYER_ID)) {
      map.addLayer({
        id: LAYER_ID,
        type: 'symbol',
        source: SOURCE_ID,
        layout: {
          'icon-image': ['get', 'icon'],
          'icon-anchor': 'bottom',
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
          'symbol-sort-key': ['to-number', ['get', 'index']],
        },
      });
    } else {
      map.setLayoutProperty(LAYER_ID, 'icon-image', ['get', 'icon']);
      map.setLayoutProperty(LAYER_ID, 'text-field', '');
      map.setLayoutProperty(LAYER_ID, 'icon-allow-overlap', true);
      map.setLayoutProperty(LAYER_ID, 'icon-ignore-placement', true);
      map.setLayoutProperty(LAYER_ID, 'symbol-sort-key', [
        'to-number',
        ['get', 'index'],
      ]);
    }

    if (!handlersBoundRef.current) {
      handlersBoundRef.current = true;
      map.on('click', LAYER_ID, (event) => {
        if (editingRef.current) return;
        const feature = event.features && event.features[0];
        if (!feature) return;
        event.originalEvent?.stopPropagation();
        setActivePointId(feature.properties.id);
      });
      map.on('click', (event) => {
        if (editingRef.current) {
          event.originalEvent?.stopPropagation();
          const id = activePointIdRef.current;
          if (!id) return;
          setDraftLocation({
            id,
            lat: event.lngLat.lat,
            lng: event.lngLat.lng,
          });
          return;
        }
        const features = map.queryRenderedFeatures(event.point, {
          layers: map.getLayer(LAYER_ID) ? [LAYER_ID] : [],
        });
        if (!features.length) setActivePointId(null);
      });
      map.on('mouseenter', LAYER_ID, () => {
        if (editingRef.current) return;
        map.getCanvas().style.cursor = 'pointer';
      });
      map.on('mouseleave', LAYER_ID, () => {
        map.getCanvas().style.cursor = editingRef.current ? 'crosshair' : '';
      });
    }
  };

  const handleLoad = (event) => {
    const map = event.target;
    mapRef.current = map;

    const start = () => {
      map.resize();
      setupLayers(map).then(() => {
        if (bounds) {
          map.fitBounds(bounds, { padding: 56, maxZoom: 15, duration: 0 });
        }
      });
    };

    if (map.isStyleLoaded && map.isStyleLoaded()) {
      start();
    } else {
      map.once('style.load', start);
    }
  };

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    Promise.all([loadPinImages(map, displayPoints), loadArrowImage(map)]).then(
      () => syncSource(map)
    );
  }, [displayPoints, activePointId]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver(() => {
      mapRef.current?.resize();
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setIsEditing(false);
    setDraftLocation(null);
  }, [activePointId]);

  useEffect(() => {
    const canvas = mapRef.current?.getCanvas?.();
    if (!canvas) return undefined;
    canvas.style.cursor = isEditing ? 'crosshair' : '';
    return () => {
      canvas.style.cursor = '';
    };
  }, [isEditing]);

  if (!items.length) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        width="100%"
        height="280px"
      >
        <AssignmentIcon sx={{ fontSize: '80px' }} color="disabled" />
        <Typography variant="h6" color="textSecondary">
          {emptyMessage}
        </Typography>
      </Box>
    );
  }

  if (!points.length) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        width="100%"
        height="280px"
      >
        <AssignmentIcon sx={{ fontSize: '80px' }} color="disabled" />
        <Typography variant="h6" color="textSecondary">
          موقعیتی برای نمایش روی نقشه وجود ندارد
        </Typography>
      </Box>
    );
  }

  return (
    <div
      ref={containerRef}
      dir="ltr"
      style={{
        direction: 'ltr',
        width: '100%',
        height: 'calc(100dvh - 210px)',
        minHeight: '360px',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Map
        token={PARSIMAP_TOKEN}
        lat={center.lat}
        lng={center.lng}
        zoom={points.length === 1 ? 14 : 11}
        mapStyle="parsimap-streets-v11"
        style={{ width: '100%', height: '100%', direction: 'ltr' }}
        onLoad={handleLoad}
      />
      <Box
        sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          zIndex: 2,
          bgcolor: 'background.paper',
          borderRadius: '10px',
          px: 1.5,
          py: 0.75,
          boxShadow: '0 4px 16px rgba(17,17,17,0.12)',
        }}
      >
        <Typography variant="caption" fontWeight={600}>
          {isEditing
            ? 'روی نقشه بزنید تا موقعیت جابه‌جا شود'
            : `${points.length} ماموریت روی نقشه`}
        </Typography>
      </Box>
      {activePoint && (
        <Paper
          elevation={0}
          sx={{
            position: 'absolute',
            left: 16,
            right: 16,
            bottom: 88,
            zIndex: 2,
            p: 1.5,
            borderRadius: '16px',
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 8px 24px rgba(17,17,17,0.12)',
            direction: 'rtl',
          }}
        >
          <Stack spacing={1}>
            <Typography variant="subtitle1" fontWeight={800}>
               {activePoint.item?.fullName || 'ماموریت'}.{activePoint.index + 1}
            </Typography>
            <Typography variant="subtitle1" color="text.primary">
              {activePoint.item?.address || 'آدرس ثبت نشده است'}
            </Typography>
            {isEditing ? (
              <>
                <Typography variant="body2" color="text.secondary">
                  روی نقشه بزنید تا موقعیت جدید انتخاب شود
                </Typography>
               
                <Stack direction="row" sx={{ gap: 1 }}>
                  <Button
                    variant="outlined"
                    onClick={cancelEdit}
                    sx={{ flex: 1, fontSize: '14px' }}
                  >
                    انصراف
                  </Button>
                  <Button
                    variant="contained"
                    onClick={saveEdit}
                    sx={{ flex: 1, fontSize: '14px' }}
                  >
                    ذخیره
                  </Button>
                </Stack>
              </>
            ) : (
              <Stack direction="row" sx={{ gap: 1 }}>
                <Button
                  variant="outlined"
                  onClick={startEdit}
                  sx={{ flex: 1, fontSize: '14px' }}
                >
                  ویرایش
                </Button>
                <Button
                  variant="contained"
                  onClick={() =>
                    handleSelect(activePoint.item, activePoint.index)
                  }
                  sx={{ flex: 1, fontSize: '14px' }}
                >
                  جزییات
                </Button>
              </Stack>
            )}
          </Stack>
        </Paper>
      )}
    </div>
  );
};

export default MissionsMapView;
